import { PaginatedResult } from '@nx-smartmonkey/shared/interfaces';
import { AnalyticsCustomersSurvey } from '../domain/analytics/AnalyticsCustomersSurvey';
import { AnalyticsDriversSurvey } from '../domain/analytics/AnalyticsDriversSurvey';
import { DateChartAnalytics } from '../domain/analytics/DateChartAnalytics';
import { HourChartAnalytics } from '../domain/analytics/HourChartAnalytics';
import { PlanStatus } from '../domain/plan/PlanStatus';
import { StopStatus } from '../domain/stop/StopStatus';
import { BinTypes } from '../interfaces/analytics';
import { analyticsRepository } from '../repositories/analytics';
import { AnalyticsRepository } from '../repositories/analytics/AnalyticsRepository';
import { sessionRepository } from '../repositories/session';
import { SessionRepository } from '../repositories/session/SessionRepository';

export class AnalyticsService {
  constructor(private analyticsRepository: AnalyticsRepository, private sessionRepository: SessionRepository) {}

  async getAnalyticsPlans({
    startDate,
    endDate,
    planStatus,
    binType,
    projectId,
  }: {
    startDate: Date;
    endDate: Date;
    planStatus?: PlanStatus;
    binType: BinTypes;
    projectId: string;
  }): Promise<DateChartAnalytics[]> {
    return this.analyticsRepository.getAnalyticsPlans({ startDate, endDate, planStatus, binType, projectId });
  }

  async getAnalyticsSentSMS({
    startDate,
    endDate,
    status,
    binType,
    projectId,
  }: {
    startDate: Date;
    endDate: Date;
    status: string;
    binType: BinTypes;
    projectId: string;
  }): Promise<DateChartAnalytics[]> {
    return this.analyticsRepository.getAnalyticsSentSMS({ startDate, endDate, status, binType, projectId });
  }

  async getAnalyticsSentEmails({
    startDate,
    endDate,
    status,
    binType,
    projectId,
  }: {
    startDate: Date;
    endDate: Date;
    status: string;
    binType: BinTypes;
    projectId: string;
  }): Promise<DateChartAnalytics[]> {
    return this.analyticsRepository.getAnalyticsSentEmails({ startDate, endDate, status, binType, projectId });
  }

  async getAnalyticsPOD({
    startDate,
    endDate,
    binType,
    vehicleId,
    sequence,
    projectId,
  }: {
    startDate: Date;
    endDate: Date;
    binType: BinTypes;
    vehicleId?: string;
    sequence: string;
    projectId: string;
  }): Promise<DateChartAnalytics[]> {
    return this.analyticsRepository.getAnalyticsPOD({
      startDate,
      endDate,
      binType,
      vehicleId,
      sequence,
      projectId,
    });
  }
  async getAnalyticsCancellationReasons({
    startDate,
    endDate,
    binType,
    vehicleId,
    sequence,
    projectId,
  }: {
    startDate: Date;
    endDate: Date;
    binType: BinTypes;
    vehicleId?: string;
    sequence: string;
    projectId: string;
  }): Promise<DateChartAnalytics[]> {
    return this.analyticsRepository.getAnalyticsCancellationReasons({
      startDate,
      endDate,
      binType,
      vehicleId,
      sequence,
      projectId,
    });
  }
  async getAnalyticsStops({
    startDate,
    endDate,
    binType,
    stopStatus,
    vehicleId,
    projectId,
  }: {
    startDate: Date;
    endDate: Date;
    binType: BinTypes;
    stopStatus?: StopStatus;
    vehicleId?: string;
    projectId: string;
  }): Promise<DateChartAnalytics[]> {
    return this.analyticsRepository.getAnalyticsStops({
      startDate,
      endDate,
      binType,
      stopStatus,
      vehicleId,
      projectId,
    });
  }
  async getAnalyticsDeliveryTime({
    startDate,
    endDate,
    vehicleId,
    reportType,
    projectId,
  }: {
    startDate: Date;
    endDate: Date;
    vehicleId?: string;
    reportType: string;
    projectId: string;
  }): Promise<HourChartAnalytics[]> {
    return this.analyticsRepository.getAnalyticsDeliveryTime({
      startDate,
      endDate,
      vehicleId,
      reportType,
      projectId,
    });
  }
  async getAnalyticsTimeOnRoute({
    startDate,
    endDate,
    binType,
    vehicleId,
    projectId,
  }: {
    startDate: Date;
    endDate: Date;
    binType: BinTypes;
    vehicleId?: string;
    projectId: string;
  }): Promise<DateChartAnalytics[]> {
    return this.analyticsRepository.getAnalyticsTimeOnRoute({
      startDate,
      endDate,
      binType,
      vehicleId,
      projectId,
    });
  }
  async getAnalyticsAverageDelays({
    startDate,
    endDate,
    binType,
    vehicleId,
    projectId,
  }: {
    startDate: Date;
    endDate: Date;
    binType: BinTypes;
    vehicleId?: string;
    projectId: string;
  }): Promise<DateChartAnalytics[]> {
    return this.analyticsRepository.getAnalyticsAverageDelays({
      startDate,
      endDate,
      binType,
      vehicleId,
      projectId,
    });
  }
  async getAnalyticsRouteTracking({
    startDate,
    endDate,
    binType,
    vehicleId,
    projectId,
  }: {
    startDate: Date;
    endDate: Date;
    binType: BinTypes;
    vehicleId?: string;
    projectId: string;
  }): Promise<DateChartAnalytics[]> {
    return this.analyticsRepository.getAnalyticsRouteTracking({
      startDate,
      endDate,
      binType,
      vehicleId,
      projectId,
    });
  }
  async getAnalyticsDeliveryAttempts({
    startDate,
    endDate,
    binType,
    vehicleId,
    projectId,
  }: {
    startDate: Date;
    endDate: Date;
    binType: BinTypes;
    vehicleId?: string;
    projectId: string;
  }): Promise<DateChartAnalytics[]> {
    return this.analyticsRepository.getAnalyticsDeliveryAttempts({
      startDate,
      endDate,
      binType,
      vehicleId,
      projectId,
    });
  }
  async getAnalyticsDriversSatisfaction({
    startDate,
    endDate,
    binType,
    vehicleId,
    projectId,
  }: {
    startDate: Date;
    endDate: Date;
    binType: BinTypes;
    vehicleId?: string;
    projectId: string;
  }): Promise<DateChartAnalytics[]> {
    return this.analyticsRepository.getAnalyticsDriversSatisfaction({
      startDate,
      endDate,
      binType,
      vehicleId,
      projectId,
    });
  }
  async getAnalyticsDriversSurveys({
    startDate,
    endDate,
    projectId,
    vehicleIds,
    offset,
    limit,
    sort,
  }: {
    startDate: Date;
    endDate: Date;
    projectId: string;
    vehicleIds?: string[];
    offset?: number;
    limit?: number;
    sort?: number;
  }): Promise<PaginatedResult<AnalyticsDriversSurvey>> {
    return this.analyticsRepository.getAnalyticsDriversSurveys({
      startDate,
      endDate,
      projectId,
      vehicleIds,
      offset,
      limit,
      sort,
    });
  }
  async getAnalyticsCustomersSatisfaction({
    startDate,
    endDate,
    binType,
    vehicleId,
    projectId,
  }: {
    startDate: Date;
    endDate: Date;
    binType: BinTypes;
    vehicleId?: string;
    projectId: string;
  }): Promise<DateChartAnalytics[]> {
    return this.analyticsRepository.getAnalyticsCustomersSatisfaction({
      startDate,
      endDate,
      binType,
      vehicleId,
      projectId,
    });
  }
  async getAnalyticsCustomersSurveys({
    startDate,
    endDate,
    projectId,
    vehicleIds,
    offset,
    limit,
    sort,
  }: {
    startDate: Date;
    endDate: Date;
    projectId: string;
    vehicleIds?: string[];
    offset?: number;
    limit?: number;
    sort?: number;
  }): Promise<PaginatedResult<AnalyticsCustomersSurvey>> {
    return this.analyticsRepository.getAnalyticsCustomersSurveys({
      startDate,
      endDate,
      projectId,
      vehicleIds,
      offset,
      limit,
      sort,
    });
  }
}

export const analyticsService = new AnalyticsService(analyticsRepository, sessionRepository);
